import React from "react";
import { AlertTriangle } from "lucide-react";

// Shuffling is no longer editable — the toggles moved out of this card. The keys
// stay so saved values round-trip until the JD gains the control; see the deferred
// entry in Docs/MCQ_ARCHITECTURE.md.
export const DEFAULT_CONFIG = {
  time_limit_minutes: 30,
  pass_percent: 60,
  shuffle_questions: true,
  shuffle_options: true,
};

// Matches the floating-label pill inputs used on the job creation page
// (SharedStep1_JobDetails) — the dark cutout color is that page's dark background,
// reused verbatim rather than re-derived.
const inputCls =
  "w-full px-6 py-3 mt-2 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200";
const labelCls =
  "absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1";

/**
 * Every field of the set on one row. Each is sized to what it holds — the name is
 * two or three words and the two settings are two-digit numbers, so only the
 * description has any use for the width a full row would give it.
 */
const SetDetailsCard = ({ draft, nameWarning, onDraftChange }) => {
  if (!draft) return null;

  const setField = (field, value) => onDraftChange({ ...draft, [field]: value });
  const setConfig = (field, value) =>
    onDraftChange({ ...draft, config: { ...draft.config, [field]: value } });

  return (
    // items-start so the duplicate-name warning appearing under the name never
    // stretches the fields beside it./
    <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-start">
      <div className="w-full sm:w-56 shrink-0 relative">
        <label className={labelCls}>
          Set Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="e.g. Python Backend Screen"
          maxLength={45}
          className={inputCls}
        />
        {/* Duplicate names are harmless now that JDs bind by id — warn, don't block. */}
        {nameWarning && (
          <p className="flex items-center gap-1.5 text-xs text-amber-400 mt-1.5">
            <AlertTriangle size={11} className="shrink-0" /> {nameWarning}
          </p>
        )}
      </div>

      <div className="flex-1 w-full min-w-[10rem] sm:max-w relative">
        <label className={labelCls}>
          Description{" "}
          <span className="text-[10px] font-normal dark:text-gray-500 text-textSecondary">
            (optional)
          </span>
        </label>
        <input
          type="text"
          value={draft.description || ""}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="What this set covers — e.g. Python and SQL basics for backend hires"
          maxLength={300}
          className={inputCls}
        />
      </div>

      <div className="w-full sm:w-44 shrink-0 relative">
        <label className={labelCls}>Time limit (min)</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft.config.time_limit_minutes ?? ""}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "");
            setConfig("time_limit_minutes", Math.min(300, parseInt(digits, 10) || 0));
          }}
          className={inputCls}
        />
      </div>

      <div className="w-full sm:w-44 shrink-0 relative">
        <label className={labelCls}>Pass mark (%)</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft.config.pass_percent ?? ""}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "");
            setConfig("pass_percent", Math.min(100, parseInt(digits, 10) || 0));
          }}
          className={inputCls}
        />
      </div>
    </div>
  );
};

export default SetDetailsCard;
